import PropTypes from 'prop-types';
import {useState} from 'react';
import {Header, Button, Modal, Message} from 'semantic-ui-react';

import httpClient from 'httpClient';

import MatomoLink from './MatomoLink';
import MatomoStatistics from './MatomoStatistics';

import styles from './RegisterInMatomo.module.scss';

const RegisterInMatomo = ({projectType, projectName, siteUrl, siteResourceName}) => {
  const [matomoData, setMatomoData] = useState();
  const [open, setOpen] = useState(false);
  const [registrationError, setRegistrationError] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const registerSite = async () => {
    setRegistrationError(null);
    try {
      setIsRegistering(true);
      if (projectType === 'shorturl') {
        const host = new URL(siteUrl).host;
        const {data} = await httpClient.post(
          `/api/web-analytics/registration/short-url/${projectName}`,
          {
            projectType,
            siteUrl,
            host,
          },
          {handleErrorsCentrally: false}
        );
        setMatomoData(data);
        setOpen(false);
        setIsRegistering(false);
        return;
      } else {
        const {data} = await httpClient.post(
          `/api/web-analytics/registration/${projectName}`,
          {
            projectType,
            siteUrl,
            siteResourceName,
          },
          {handleErrorsCentrally: false}
        );
        setMatomoData(data);
      }

      setOpen(true);
      setIsRegistering(false);
    } catch (err) {
      let registrationErrorMessage = '';
      if (err.response.data.errors) {
        registrationErrorMessage += JSON.stringify(err.response.data.errors.json);
      }
      if (err.response.data.error) {
        registrationErrorMessage += err.response.data.error;
      }
      setIsRegistering(false);
      setRegistrationError(registrationErrorMessage);
    }
  };

  if (matomoData) {
    return (
      <div className={styles.RegisterInMatomo}>
        <Modal
          closeIcon
          open={open}
          onClose={() => {
            setOpen(false);
          }}
        >
          <Header content="Matomo Registration" />
          {projectType === 'webeos' && (
            <Modal.Content>
              <p>
                The site "<strong>{siteUrl}</strong>" was successfully registered for Matomo Web
                Analytics. In order to start gathering data, make sure to include the following code
                snippet on every page of your website. We recommend pasting it immediately before
                the closing {'</head>'} tag.
              </p>
              <div className={styles.CodeBlock}>
                <pre>{matomoData.javascriptTag}</pre>
              </div>
            </Modal.Content>
          )}
          {projectType === 'drupal' && (
            <Modal.Content>
              <p>
                The site "<strong>{siteUrl}</strong>" was successfully registered for Matomo Web
                Analytics. In order to start gathering data, make sure to configure{' '}
                <strong>Matomo Analytics</strong> module in your{' '}
                <a
                  className={styles.Link}
                  href={`https://${siteUrl}/admin/config/system/matomo`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Drupal configuration page
                </a>{' '}
                by setting the <strong>Matomo site ID</strong> to{' '}
                <strong>{matomoData.siteId}</strong>.
              </p>
            </Modal.Content>
          )}
          {projectType === 'wordpress' && (
            <Modal.Content>
              <p>
                The site "<strong>{siteUrl}</strong>" was successfully registered for{' '}
                <a
                  href="https://cern.service-now.com/service-portal?id=kb_article&n=KB0009092"
                  target="_blank"
                  rel="noreferrer"
                >
                  Matomo Web Analytics
                </a>
                , in a few minutes it will start tracking traffic and it can be seen{' '}
                <a
                  href={
                    'https://webanalytics.web.cern.ch/index.php?module=CoreHome&action=index&idSite=' +
                    matomoData.siteId
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  here
                </a>
                .
              </p>
            </Modal.Content>
          )}
        </Modal>
        <MatomoLink siteId={matomoData.siteId} />
        <MatomoStatistics siteId={matomoData.siteId} columns={projectType === 'webeos' ? 2 : 1} />
      </div>
    );
  } else {
    return (
      <div className={styles.RegisterInMatomo}>
        <Button
          floated="right"
          type="button"
          className={styles.ActionButton}
          content="Register site for Matomo"
          onClick={() => {
            registerSite();
          }}
          disabled={!!registrationError || isRegistering}
          loading={isRegistering}
        />

        {registrationError && (
          <Message className={styles.Message} negative>
            <Message.Header>
              There was a problem completing the registration of the site in Matomo.
            </Message.Header>
            <p>{registrationError}</p>
            <p>
              Please try again or open a{' '}
              <a
                href="https://cern.service-now.com/service-portal?id=service_element&name=web-portal"
                target="_blank"
                rel="noreferrer"
              >
                ticket
              </a>
            </p>
          </Message>
        )}
      </div>
    );
  }
};

RegisterInMatomo.propTypes = {
  projectType: PropTypes.string.isRequired,
  projectName: PropTypes.string.isRequired,
  siteUrl: PropTypes.string.isRequired,
  siteResourceName: PropTypes.string,
};

export default RegisterInMatomo;
